<script setup>
// GRUPO DE ETIQUETAS (v77, so id 1): criar grupos, vincular etiquetas
// existentes e criar etiquetas ja dentro do grupo. Os grupos sao usados na
// aba Etiquetas das caixas de entrada (vinculo em lote).
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import LabelGroupsAPI from 'dashboard/api/labelGroups';

const emit = defineEmits(['close']);

const store = useStore();
const getters = useStoreGetters();
const allLabels = computed(() => getters['labels/getLabels'].value);

const groups = ref([]);
const isLoading = ref(false);
const selectedGroupId = ref(null);
const newGroupName = ref('');
const editName = ref('');
const editLabelIds = ref({});
const newLabelTitle = ref('');
const isSaving = ref(false);

// Paleta padrao das etiquetas do Chatwoot pra etiqueta criada dentro do grupo
const LABEL_COLORS = [
  '#28AD21', '#03bcd4', '#fc0d1b', '#8b5cf6', '#f59e0b',
  '#ef476f', '#11998e', '#243b55', '#ffc107', '#7a3f6d',
];

const selectedGroup = computed(
  () => groups.value.find(g => g.id === selectedGroupId.value) || null
);

const fetchGroups = async () => {
  isLoading.value = true;
  try {
    const { data } = await LabelGroupsAPI.list();
    groups.value = data.data || [];
  } catch (e) {
    useAlert(e?.response?.data?.error || e.message);
  } finally {
    isLoading.value = false;
  }
};

const selectGroup = group => {
  selectedGroupId.value = group.id;
  editName.value = group.name;
  editLabelIds.value = {};
  group.label_ids.forEach(id => {
    editLabelIds.value[id] = true;
  });
};

const createGroup = async () => {
  const name = newGroupName.value.trim();
  if (!name) return;
  isSaving.value = true;
  try {
    const { data } = await LabelGroupsAPI.create(name, []);
    newGroupName.value = '';
    await fetchGroups();
    const created = groups.value.find(g => g.id === data.id);
    if (created) selectGroup(created);
    useAlert(`Grupo "${name}" criado.`);
  } catch (e) {
    useAlert(e?.response?.data?.error || e.message);
  } finally {
    isSaving.value = false;
  }
};

const saveGroup = async () => {
  if (!selectedGroup.value) return;
  isSaving.value = true;
  try {
    const labelIds = Object.keys(editLabelIds.value)
      .filter(id => editLabelIds.value[id])
      .map(Number);
    await LabelGroupsAPI.update(selectedGroup.value.id, {
      name: editName.value.trim(),
      label_ids: labelIds,
    });
    await fetchGroups();
    useAlert('Grupo salvo.');
  } catch (e) {
    useAlert(e?.response?.data?.error || e.message);
  } finally {
    isSaving.value = false;
  }
};

const deleteGroup = async () => {
  if (!selectedGroup.value) return;
  // eslint-disable-next-line no-alert
  if (!window.confirm(`Excluir o grupo "${selectedGroup.value.name}"? As etiquetas continuam existindo.`)) return;
  try {
    await LabelGroupsAPI.delete(selectedGroup.value.id);
    selectedGroupId.value = null;
    await fetchGroups();
    useAlert('Grupo excluído.');
  } catch (e) {
    useAlert(e?.response?.data?.error || e.message);
  }
};

// Cria a etiqueta na conta e ja marca no grupo selecionado
const createLabelInGroup = async () => {
  const title = newLabelTitle.value.trim().toLowerCase().replace(/\s+/g, '_');
  if (!title || !selectedGroup.value) return;
  isSaving.value = true;
  try {
    const color = LABEL_COLORS[title.length % LABEL_COLORS.length];
    const created = await store.dispatch('labels/create', {
      title,
      color,
      show_on_sidebar: true,
    });
    const createdId = created?.id;
    if (createdId) editLabelIds.value[createdId] = true;
    newLabelTitle.value = '';
    useAlert(`Etiqueta "${title}" criada — clique em Salvar grupo para gravar o vínculo.`);
  } catch (e) {
    useAlert(e?.message || 'Erro ao criar etiqueta');
  } finally {
    isSaving.value = false;
  }
};

onMounted(fetchGroups);
</script>

<template>
  <div
    class="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
    @click.self="emit('close')"
  >
    <div
      class="bg-n-solid-2 rounded-xl p-5 w-full max-w-3xl max-h-[85vh] overflow-y-auto shadow-xl"
    >
      <div class="flex items-start justify-between mb-4">
        <div>
          <h3 class="text-lg font-medium text-n-slate-12">
            Grupos de etiquetas
          </h3>
          <p class="text-sm text-n-slate-11">
            Monte conjuntos de etiquetas para vincular às caixas de entrada de
            uma vez (aba Etiquetas da caixa). Uma etiqueta pode estar em vários
            grupos.
          </p>
        </div>
        <NextButton
          variant="ghost"
          color="slate"
          icon="i-lucide-x"
          @click="emit('close')"
        />
      </div>

      <div class="grid grid-cols-3 gap-4">
        <!-- lista de grupos + criar -->
        <div class="col-span-1 flex flex-col gap-2">
          <div class="flex gap-1">
            <input
              v-model="newGroupName"
              class="w-full h-9 rounded-lg border border-n-weak bg-n-alpha-black2 px-2 text-sm text-n-slate-12 outline-none"
              placeholder="Nome do novo grupo"
              @keyup.enter="createGroup"
            />
            <NextButton
              size="sm"
              label="Criar"
              :is-loading="isSaving"
              :disabled="!newGroupName.trim()"
              @click="createGroup"
            />
          </div>
          <p v-if="isLoading" class="text-xs text-n-slate-11">Carregando…</p>
          <p
            v-else-if="!groups.length"
            class="text-xs text-n-slate-11"
          >
            Nenhum grupo ainda.
          </p>
          <button
            v-for="g in groups"
            :key="g.id"
            class="text-left rounded-lg border px-3 py-2 text-sm"
            :class="
              g.id === selectedGroupId
                ? 'border-n-brand text-n-slate-12 bg-n-alpha-black2'
                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-black1'
            "
            @click="selectGroup(g)"
          >
            {{ g.name }}
            <span class="text-xs text-n-slate-10">
              ({{ g.label_ids.length }})
            </span>
          </button>
        </div>

        <!-- edicao do grupo selecionado -->
        <div class="col-span-2">
          <div
            v-if="!selectedGroup"
            class="text-sm text-n-slate-11 border border-n-weak rounded-lg p-6 text-center"
          >
            Selecione um grupo à esquerda (ou crie um) para vincular etiquetas.
          </div>
          <div v-else class="flex flex-col gap-3">
            <div class="flex gap-2 items-center">
              <input
                v-model="editName"
                class="w-full h-9 rounded-lg border border-n-weak bg-n-alpha-black2 px-2 text-sm text-n-slate-12 outline-none"
              />
              <NextButton
                size="sm"
                variant="outline"
                color="ruby"
                label="Excluir"
                @click="deleteGroup"
              />
            </div>

            <div class="flex gap-1">
              <input
                v-model="newLabelTitle"
                class="w-full h-9 rounded-lg border border-n-weak bg-n-alpha-black2 px-2 text-sm text-n-slate-12 outline-none"
                placeholder="Criar nova etiqueta neste grupo (ex: promo_julho)"
                @keyup.enter="createLabelInGroup"
              />
              <NextButton
                size="sm"
                variant="outline"
                label="Criar etiqueta"
                :is-loading="isSaving"
                :disabled="!newLabelTitle.trim()"
                @click="createLabelInGroup"
              />
            </div>

            <div
              class="border border-n-weak rounded-lg p-3 max-h-72 overflow-y-auto grid grid-cols-2 gap-1"
            >
              <label
                v-for="label in allLabels"
                :key="label.id"
                class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer py-0.5"
              >
                <input
                  v-model="editLabelIds[label.id]"
                  type="checkbox"
                  class="accent-current"
                />
                <span
                  class="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                  :style="{ backgroundColor: label.color }"
                />
                <span class="truncate">{{ label.title }}</span>
              </label>
            </div>

            <div class="flex justify-end">
              <NextButton
                label="Salvar grupo"
                :is-loading="isSaving"
                @click="saveGroup"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
